import React, { useState, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { FiBarChart2 } from 'react-icons/fi';

const ActiveUsersChart = ({ chartType, setChartType, activeUsersGraphData, analyticsData, isLoading }) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: '',
    details: null,
    xAlign: 'center',
  });

  const formatTimeReadable = (secs) => {
    if (!secs) return '0s';
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const m = mins % 60;
    return `${hrs}h ${m}m`;
  };

  const getOptions = (isBar) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false, // Turn off native canvas tooltip
        external: (context) => {
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            return;
          }

          const item = tooltipModel.dataPoints?.[0];
          if (!item) return;

          const details = item.dataset.dailyDetails?.[item.dataIndex];
          setTooltip({
            visible: true,
            x: tooltipModel.caretX,
            y: tooltipModel.caretY - 12,
            title: tooltipModel.title?.[0] || '',
            value: item.formattedValue,
            details,
            xAlign: tooltipModel.xAlign || 'center',
          });
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: '#F3F4F6', drawBorder: false },
        ticks: {
          color: '#9CA3AF',
          font: { size: 11 },
          maxTicksLimit: 6,
          precision: 0 // Enforces whole numbers on Y-axis
        },
        border: { display: false },
      },
      x: {
        grid: { display: false },
        ticks: { color: '#9CA3AF', font: { size: 11 }, maxTicksLimit: 8 },
        border: { display: false },
      },
    },
    elements: isBar
      ? { bar: { borderRadius: 6 } }
      : {},
  });

  const transformStyle = useMemo(() => {
    if (tooltip.xAlign === 'left') return 'translate(-15%, -100%)';
    if (tooltip.xAlign === 'right') return 'translate(-85%, -100%)';
    return 'translate(-50%, -100%)';
  }, [tooltip.xAlign]);

  const caretLeftStyle = useMemo(() => {
    if (tooltip.xAlign === 'left') return '15%';
    if (tooltip.xAlign === 'right') return '85%';
    return '50%';
  }, [tooltip.xAlign]);

  const lineOptions = useMemo(() => getOptions(false), []);
  const barOptions = useMemo(() => getOptions(true), []);

  const dailyUsers = analyticsData?.dailyEngagement?.map(item => item.uniqueUserCount ?? item.uniqueUsers ?? 0) || [];
  const avgDauVal = dailyUsers.length > 0
    ? Math.round(dailyUsers.reduce((a, b) => a + b, 0) / dailyUsers.length)
    : 0;
  const dau = avgDauVal.toLocaleString();

  const wauVal = analyticsData?.uniqueUsers || 0;
  const wau = wauVal.toLocaleString();

  const mau = (analyticsData?.uniqueUsers || analyticsData?.totalUsers || 0).toLocaleString();

  const metrics = [
    { label: 'DAU', sublabel: 'Daily Avg', value: dau, color: 'text-[#CC9900]', bg: 'bg-[#CC9900]/8 border-[#CC9900]/15' },
    { label: 'WAU', sublabel: 'Weekly Avg', value: wau, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
    { label: 'MAU', sublabel: 'Monthly', value: mau, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-100' },
  ];

  return (
    <div className="chart-card h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-5 ">
        <div>
          <p className="section-eyebrow mb-1">Engagement</p>
          <h3 className="text-lg font-bold text-[#111827] flex items-center gap-2">
            <FiBarChart2 className="text-[#CC9900]" /> Active Users Overview
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Daily, weekly & monthly engagement — filtered by selected period.
          </p>
        </div>

        {/* Chart type toggle */}
        <div className="flex-shrink-0 flex items-center gap-1 p-1 bg-[#F9FAFB] rounded-[14px] border border-[#F2F4F7]">
          {[['line', 'Area'], ['bar', 'Bar']].map(([type, label]) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`px-3 py-1.5 text-xs font-bold rounded-[10px] transition-all duration-300 ${chartType === type
                ? 'bg-white text-[#CC9900] shadow-sm ring-1 ring-black/[0.04]'
                : 'text-[#667085] hover:text-[#101828] hover:bg-white/50'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>





       {/* Chart */}
      <div className="h-[280px] w-full relative flex items-center justify-center">
        {isLoading || !analyticsData ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-gray-100 border-t-[#CC9900] rounded-full animate-spin" />
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Collecting Report...</p>
          </div>
        ) : chartType === 'line' ? (
          <Line data={activeUsersGraphData} options={lineOptions} />
        ) : (
          <Bar data={activeUsersGraphData} options={barOptions} />
        )}

        {/* React Custom HTML Tooltip */}
        {tooltip.visible && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              transform: transformStyle,
              pointerEvents: 'none',
              zIndex: 50,
              background: 'rgba(15, 23, 42, 0.98)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              padding: '12px 16px',
              color: '#F8FAFC',
              fontSize: '12px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)',
              minWidth: '200px',
              fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
          >
            {/* Date Header */}
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
              {tooltip.title}
            </div>

            {/* Active Users */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '6px', marginBottom: '8px' }}>
              <span style={{ color: '#E2E8F0', fontWeight: 600 }}>Active Users</span>
              <span style={{ fontWeight: 800, color: '#CC9900', fontSize: '14px' }}>{tooltip.value}</span>
            </div>

            {/* Detailed Stats */}
            {tooltip.details && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>Sessions</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{tooltip.details.totalSessions?.toLocaleString() ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>App Clicks</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{tooltip.details.totalClicks?.toLocaleString() ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>Venue Clicks</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{tooltip.details.totalVenueClicks?.toLocaleString() ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>App Time</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{formatTimeReadable(tooltip.details.totalAppTime)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>Venue Time</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{formatTimeReadable(tooltip.details.totalVenueTime)}</span>
                </div>
              </div>
            )}

            {/* Arrow caret pointing down */}
            <div style={{
              position: 'absolute',
              bottom: '-6px',
              left: caretLeftStyle,
              transform: 'translateX(-50%) rotate(45deg)',
              width: '12px',
              height: '12px',
              background: 'rgba(15, 23, 42, 0.98)',
              borderRight: '1px solid rgba(255, 255, 255, 0.15)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
              pointerEvents: 'none',
            }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ActiveUsersChart);
